import Decimal from 'decimal.js';

import { formatMoney, isSafeNumber, unitDivisor } from './currency';
import { normalizeManagedStockQuantity } from './listingRules';
import { types as sdkTypes } from './sdkLoader';

const { Money } = sdkTypes;

export const TIMED_SALE_BUYER_COMMISSION_PERCENTAGE = 6;
export const TIMED_SALE_SELLER_COMMISSION_PERCENTAGE = 10;

const normalizePositiveQuantity = quantity => {
  const normalizedQuantity =
    quantity && typeof quantity === 'object' && typeof quantity.toNumber === 'function'
      ? quantity.toNumber()
      : quantity;
  const parsed =
    typeof normalizedQuantity === 'string' && normalizedQuantity.trim() !== ''
      ? Number(normalizedQuantity)
      : normalizedQuantity;

  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const getListingBidQuantity = listing => {
  const quantity = normalizeManagedStockQuantity(listing?.currentStock?.attributes?.quantity);
  return normalizePositiveQuantity(quantity);
};

export const getBidUnitLabels = listing => {
  const publicData = listing?.attributes?.publicData || {};
  const listingType = String(publicData?.listingType || '').toLowerCase();
  const isBarrelListing = Boolean(
    listingType.includes('spirit') ||
      publicData?.spirit_type ||
      publicData?.producer ||
      publicData?.mashbill
  );

  return isBarrelListing
    ? { singular: 'barrel', plural: 'barrels' }
    : { singular: 'unit', plural: 'units' };
};

export const moneyFromBidInput = (value, currency) => {
  const valueString = typeof value === 'number' ? String(value) : value;

  if (typeof valueString !== 'string' || !valueString.trim() || !currency) {
    return null;
  }

  try {
    const amount = new Decimal(valueString.trim()).times(unitDivisor(currency));

    if (!amount.isFinite() || !amount.isInteger() || amount.lte(0) || !isSafeNumber(amount)) {
      return null;
    }

    return new Money(amount.toNumber(), currency);
  } catch (e) {
    return null;
  }
};

export const multiplyMoneyByQuantity = (money, quantity) => {
  const normalizedQuantity = normalizePositiveQuantity(quantity);

  if (!money?.currency || !Number.isInteger(money?.amount) || !normalizedQuantity) {
    return null;
  }

  const totalAmount = new Decimal(money.amount).times(normalizedQuantity);

  if (!totalAmount.isFinite() || !totalAmount.isInteger() || !isSafeNumber(totalAmount)) {
    return null;
  }

  return new Money(totalAmount.toNumber(), money.currency);
};

const percentageMoney = (money, percentage) => {
  const normalizedPercentage = Number(percentage);

  if (
    !money?.currency ||
    !Number.isInteger(money?.amount) ||
    !Number.isFinite(normalizedPercentage)
  ) {
    return null;
  }

  const feeAmount = new Decimal(money.amount)
    .times(normalizedPercentage)
    .dividedBy(100)
    .toNearest(1, Decimal.ROUND_HALF_UP);

  if (!feeAmount.isFinite() || !feeAmount.isInteger() || !isSafeNumber(feeAmount)) {
    return null;
  }

  return new Money(feeAmount.toNumber(), money.currency);
};

const addMoney = (left, right) => {
  if (!left?.currency || left.currency !== right?.currency) {
    return null;
  }

  const amount = new Decimal(left.amount).plus(right.amount);

  if (!amount.isFinite() || !amount.isInteger() || !isSafeNumber(amount)) {
    return null;
  }

  return new Money(amount.toNumber(), left.currency);
};

const subtractMoney = (left, right) => {
  if (!left?.currency || left.currency !== right?.currency) {
    return null;
  }

  const amount = new Decimal(left.amount).minus(right.amount);

  if (!amount.isFinite() || !amount.isInteger() || !isSafeNumber(amount)) {
    return null;
  }

  return new Money(amount.toNumber(), left.currency);
};

export const getTimedSaleFeeBreakdown = hammerPrice => {
  if (!hammerPrice?.currency || !Number.isInteger(hammerPrice?.amount)) {
    return null;
  }

  const buyerCommission = percentageMoney(hammerPrice, TIMED_SALE_BUYER_COMMISSION_PERCENTAGE);
  const sellerCommission = percentageMoney(hammerPrice, TIMED_SALE_SELLER_COMMISSION_PERCENTAGE);
  const buyerTotal = buyerCommission ? addMoney(hammerPrice, buyerCommission) : null;
  const sellerNet = sellerCommission ? subtractMoney(hammerPrice, sellerCommission) : null;

  if (!buyerCommission || !sellerCommission || !buyerTotal || !sellerNet) {
    return null;
  }

  return {
    hammerPrice,
    buyerCommission,
    buyerTotal,
    sellerCommission,
    sellerNet,
  };
};

export const getBidPricingBreakdown = ({ bidInput, currency, quantity }) => {
  const normalizedQuantity = normalizePositiveQuantity(quantity);
  const unitPrice = moneyFromBidInput(bidInput, currency);
  const total = unitPrice ? multiplyMoneyByQuantity(unitPrice, normalizedQuantity) : null;
  const hammerPrice = total || unitPrice;
  const timedSaleFees = getTimedSaleFeeBreakdown(hammerPrice);

  return {
    quantity: normalizedQuantity,
    unitPrice,
    total,
    hammerPrice,
    timedSaleFees,
    showTotal: Boolean(normalizedQuantity && normalizedQuantity > 1),
    showTimedSaleFees: Boolean(timedSaleFees),
  };
};

export const formatBidMoney = (intl, money) => {
  if (!money) {
    return '-';
  }

  try {
    return formatMoney(intl, money);
  } catch (e) {
    return money.currency || '-';
  }
};
